import { createApi } from '@reduxjs/toolkit/query/react';
import { saveAs } from 'file-saver';
import { createBaseQuery, toQueryParams } from 'helpers/api';

export const refereesApi = createApi({
  reducerPath: 'refereesApi',
  baseQuery: createBaseQuery('referees', { timeout: 0 }),
  endpoints: builder => ({
    createReferee: builder.mutation({
      query: data => ({
        url: '/',
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateReferee: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    deleteReferee: builder.mutation({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
    }),
    sendRefereeRequestEmail: builder.mutation({
      query: arg => {
        const id = typeof arg === 'object' && arg !== null ? arg.id : arg;
        const template_id = typeof arg === 'object' && arg !== null ? arg.template_id : undefined;
        return {
          url: `/${id}/request`,
          method: 'POST',
          body: { template_id: template_id ?? null },
        };
      },
      transformResponse: ({ result }) => result,
    }),
    sendRefereeRequestEmailByToken: builder.mutation({
      query: token => ({
        url: `/token/${token}/request`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
    }),
    sendReplacementRequest: builder.mutation({
      query: ({ id, template_id, message }) => ({
        url: `/${id}/request/replacement`,
        method: 'POST',
        body: { template_id: template_id ?? null, message: message ?? null },
      }),
      transformResponse: ({ result }) => result,
    }),
    getPdfReport: builder.query({
      queryFn: async ({ id, filename }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/${id}/report_pdf`,
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });

        if (response.error) {
          return { error: response.error };
        }

        saveAs(response.data, filename);
        return { data: null };
      },
    }),
    getReportPdfDataUrl: builder.query({
      queryFn: async (id, api, extraOptions, baseQuery) => {
        const { data: blob } = await baseQuery({
          url: `/${id}/report_pdf`,
          responseHandler: async response => await response.blob(),
          cache: 'no-cache',
        });
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        return { data: dataUrl };
      },
    }),
    getCustomPdfReport: builder.query({
      queryFn: async ({ id, filename }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/${id}/report_custom_pdf`,
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });
        if (response.error) {
          return { error: response.error };
        }

        saveAs(response.data, filename);
        return { data: null };
      },
    }),
    getRefereeReportUrl: builder.query({
      query: id => `/${id}/report_url`,
      transformResponse: ({ result }) => result,
    }),
    getRefereeTypeformParams: builder.query({
      query: id => `/${id}/typeform`,
      transformResponse: ({ result }) => result,
    }),
    getRefereeFormByToken: builder.query({
      query: token => `/token/${token}/form`,
      transformResponse: ({ result }) => result,
    }),
    getQuestionUpload: builder.query({
      queryFn: async ({ id, url }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/${id}/report/question/upload?url=${url}`,
          responseHandler: async response => {
            const [, filename] = response.headers['content-disposition'].split('filename=');
            const blob = await response.blob();
            return { file: blob, filename };
          },
          cache: 'no-cache',
        });

        if (response.error) {
          return { error: response.error };
        }

        saveAs(response.data.file, response.data.filename);
        return { data: null };
      },
    }),
    getQuestionUploadByToken: builder.query({
      queryFn: async ({ token, url }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/token/${token}/report/question/upload?url=${url}`,
          responseHandler: async response => {
            const [, filename] = response.headers['content-disposition'].split('filename=');
            const blob = await response.blob();
            return { file: blob, filename };
          },
          cache: 'no-cache',
        });

        if (response.error) {
          return { error: response.error };
        }

        saveAs(response.data.file, response.data.filename);
        return { data: null };
      },
    }),
    getRefereeResponse: builder.query({
      query: id => `/${id}/answers`,
      transformResponse: ({ result }) => result,
    }),
    getReportData: builder.query({
      query: token => `/token/${token}/report`,
      transformResponse: ({ result }) => result,
    }),
    getRefereeBrandByToken: builder.query({
      query: token => `/token/${token}/brand`,
      transformResponse: ({ result }) => result,
    }),
    getRefereeByToken: builder.query({
      query: token => `/token/${token}`,
      transformResponse: ({ result }) => result,
    }),
    getCompanyCheckConfigByToken: builder.query({
      query: token => `/token/${token}/company_check_config`,
      transformResponse: response => response?.result ?? null,
    }),
    saveFollowUpQuestion: builder.mutation({
      query: ({ token, follow_up_answer }) => ({
        url: `/token/${token}/followUpQuestion`,
        method: 'POST',
        body: { follow_up_answer },
      }),
    }),
    saveUtmSource: builder.mutation({
      query: ({ token, utm_source }) => ({
        url: `/token/${token}/history`,
        method: 'POST',
        body: { utm_source },
      }),
    }),
    verifyReferee: builder.query({
      query: ({ token, linkedin_profile, user_agent }) =>
        `/token/${token}/typeform?linkedin_profile=${linkedin_profile}&user_agent=${user_agent}`,
      transformResponse: ({ result }) => result,
    }),
    saveResponse: builder.mutation({
      query: ({ token, params }) => ({
        url: `/token/${token}`,
        method: 'POST',
        body: params,
      }),
    }),
    getRefereeUpload: builder.query({
      query: ({ token, id }) => `/token/${token}/uploads/${id}`,
      transformResponse: ({ result }) => result,
    }),
    signRefereeUpload: builder.query({
      query: ({ token, params }) => `/token/${token}/uploads/sign?${toQueryParams(params)}`,
      transformResponse: ({ result }) => result,
    }),
    createRefereeUpload: builder.mutation({
      query: ({ token, params }) => ({
        url: `/token/${token}/uploads`,
        method: 'POST',
        body: params,
      }),
      transformResponse: ({ result }) => result,
    }),
    deleteRefereeUpload: builder.mutation({
      query: ({ token, id }) => ({
        url: `/token/${token}/uploads/${id}`,
        method: 'DELETE',
      }),
    }),
    detectTextLanguage: builder.query({
      query: ({ token, text }) => ({
        url: `/token/${token}/language_detection`,
        method: 'POST',
        body: { text: text },
      }),
      transformResponse: ({ result }) => result,
    }),
    translateText: builder.query({
      query: ({ token, params }) => ({
        url: `/token/${token}/translate`,
        method: 'POST',
        body: params,
      }),
      transformResponse: ({ result }) => result,
    }),
  }),
});

export const {
  useLazyGetRefereeUploadQuery,
  useLazySignRefereeUploadQuery,
  useCreateRefereeUploadMutation,
  useDeleteRefereeUploadMutation,
  useSaveResponseMutation,
  useLazyVerifyRefereeQuery,
  useLazyGetRefereeFormByTokenQuery,
  useSaveUtmSourceMutation,
  useGetRefereeByTokenQuery,
  useSendRefereeRequestEmailByTokenMutation,
  useSaveFollowUpQuestionMutation,
  useGetRefereeBrandByTokenQuery,
  useGetCompanyCheckConfigByTokenQuery,
  useLazyGetQuestionUploadByTokenQuery,
  useLazyGetReportDataQuery,
  useDeleteRefereeMutation,
  useGetRefereeResponseQuery,
  useLazyGetQuestionUploadQuery,
  useLazyGetRefereeTypeformParamsQuery,
  useCreateRefereeMutation,
  useUpdateRefereeMutation,
  useSendRefereeRequestEmailMutation,
  useSendReplacementRequestMutation,
  useLazyGetPdfReportQuery,
  useGetReportPdfDataUrlQuery,
  useLazyDetectTextLanguageQuery,
  useLazyTranslateTextQuery,
  useLazyGetCustomPdfReportQuery,
  useLazyGetRefereeReportUrlQuery,
} = refereesApi;
